import { mergeAttributes } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { SuggestionOptions } from "@tiptap/suggestion";
import renderRecordMentionOptions from "./render-record-mention-options";
import type { RecordMentionItem } from "./types";

/** Caller supplies `items`; other suggestion fields optional and merge with defaults (e.g. `allowSpaces`). */
type RecordMentionSuggestion = {
  items: NonNullable<SuggestionOptions<RecordMentionItem>["items"]>;
} & Partial<Omit<SuggestionOptions<RecordMentionItem>, "items" | "editor">>;

type RecordMentionOptions = {
  suggestion: RecordMentionSuggestion;
};

export const RecordMention = Mention.extend<RecordMentionOptions>({
  name: "recordMention",

  addOptions() {
    return {
      ...this.parent?.(),
      renderText: ({ options, node }: { options: RecordMentionOptions; node: ProseMirrorNode }) => {
        const ch = options.suggestion?.char ?? "@";
        return `${ch}${String(node.attrs.label ?? node.attrs.id ?? "")}`;
      },
      suggestion: {
        char: "@",
        startOfLine: false,
        allowSpaces: true,
        render: renderRecordMentionOptions,
        items: async () => [],
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
      },
      source: {
        default: null,
      },
      label: {
        default: null,
      },
      icon: {
        default: null,
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const triggerChar = this.options.suggestion?.char ?? "@";
    const label = node.attrs.label ?? node.attrs.id ?? "";
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": "record-mention",
        "data-record-mention": "true",
        "data-record-id": node.attrs.id,
        "data-record-source": node.attrs.source,
        "data-record-label": node.attrs.label,
        contenteditable: "false",
      }),
      `${triggerChar}${label}`,
    ];
  },
}).configure({
  suggestion: {
    allowSpaces: true,
    render: renderRecordMentionOptions,
    items: async () => [],
  },
});

export function createRecordMentionExtension(suggestion: RecordMentionSuggestion) {
  return RecordMention.configure({
    suggestion: {
      char: "@",
      startOfLine: false,
      allowSpaces: true,
      render: renderRecordMentionOptions,
      command: ({ editor, range, props }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: "recordMention",
              attrs: {
                id: props.id,
                source: props.source,
                label: props.label || props.id,
                icon: props.icon || null,
              },
            },
            { type: "text", text: " " },
          ])
          .run();
      },
      ...suggestion,
    },
  });
}
